<script lang="ts" setup>
import { getImageFile, getVideoFile, isInTimeRange, sleep } from '@/utils';
import { computed, onMounted, nextTick, ref, Ref, toRefs, watch } from 'vue';
import { useScene } from './useScene';
import { useBall } from './useBall';
import { usePlane } from './usePlane';
import { useRoute } from 'vue-router';
import { TK } from '@/utils/tracker';
import { PageEnum } from '@/enums/pageEnum';
import { playAudio } from '@/utils/audio';
import { useEventBus, useIntervalFn, useTimeoutFn } from '@vueuse/core';
import { getScale, getCenterScale } from './config';
import { createProgress, lerp } from '@/utils/math';
import { navigateTo, redirectTo } from '@/utils/page';
import WebglFailPopup from '@/components/WebglFailPopup/index.vue';

const props = defineProps({
    width: {
        type: Number,
        default: 640,
    },
    height: {
        type: Number,
        default: 640,
    }
});
const { width, height } = toRefs(props);
const canvasWidth = computed(() => width.value);
const canvasHeight = computed(() => width.value / 0.45);
const sceneRef: Ref<HTMLElement | null> = ref(null);
const renderStatus = ref(0); // 0 未定 1 成功 2 失败
function handleInitWebglError(error: string) {
    // console.log('init render error', error);
    renderStatus.value = 2;
    useEventBus('RENDER_INIT_FAIL').emit();
}
const { 
    scene, 
    camera, 
    renderScene, 
    renderBloom, 
    renderStats,
    setBloomStatus,
    putEnvironmentAtScene
} = useScene(sceneRef, canvasWidth, canvasHeight, handleInitWebglError);
const { 
    showLight,
    putBallAtScene,
    resetModel,
    showByMicro,
    moveToCenter,
    getLightList,
    rotateZ,
    renderParticles,
    createBindModelEvent,
} = useBall(scene, { onRotate });
const { loadPlaneTexture, showPlane, hidePlane } = usePlane(scene);
const modelEvent = createBindModelEvent(camera, (list) => {
    if (list.some(item => item.object.name.includes('Sphere'))) {
        playBallInCenter();
    }
});
async function playBallInCenter() {
    if (!showBalls.value) return;
    TK('scan_ball_click', PageEnum.MICRO, true);
    playVideo();
    hidePlane();
    showMilkFace.value = false;
    showBalls.value = false;
    showAperture.value = false;
    showClickBallTip.value = false;
    showImageContainer.value = false;
    renderBloom();
    setBloomStatus(true);
    await moveToCenter();
    TK('result_view', PageEnum.BALL);
    renderParticles();
    showEndBtn.value = true;
    await sleep(1000);
    showEndSlogan.value = true;
    showLight();
}
const showModel = ref(false);
const showBalls = ref(false);
const showImageContainer = ref(true);
const showEndBtn = ref(false);
const showEndSlogan = ref(false);

const keyStart = 9;
const keyEnd = 29;
const key = ref(keyStart);
const keyFramePrefix = 'render/milk';
const showOneCup = ref(true);
const imageScale = 2.2;
const imageOffsetY = -70;
function getKeyFrameFileName(index: number) {
    return `${keyFramePrefix + index.toString().padStart(4, '0')}.png`
}
const preventClickCup = computed(() => key.value !== keyStart);
const showImageList = computed(() => new Array(keyEnd - keyStart + 1).fill(0).map((_, index) => index + 1).filter(item => item >= key.value + 1 - keyStart))
function playKeyFrame() {
    TK('scan_cupAni_view', PageEnum.MICRO);
    if (!showOneCup.value) return;
    showOneCup.value = false;
    let lastTime = performance.now();
    const frameDelay = 1000 / 24;
    function animate(currentTime: number) {
        const elapsedTime = currentTime - lastTime;
        if (elapsedTime >= frameDelay) {
            key.value++;
            lastTime = currentTime;
        }
        if (key.value < keyEnd) {
            requestAnimationFrame(animate);
        } else {
            showMicroContainer();
            TK('scan_cupAni_show', PageEnum.MICRO);
        }
    }
    animate(performance.now());
}

const route = useRoute();
watch(() => route.hash, async () => {
    if (route.hash === '#Model' || route.hash === '#model') {
        // await sleep(1000);
        // playKeyFrame();
    }
});

const webglFailPopupRef: Ref<InstanceType<typeof WebglFailPopup> | null> = ref(null);
async function load() {
    if (renderStatus.value === 2) webglFailPopupRef.value?.showPopup();
    if (renderStatus.value === 0) useEventBus('RENDER_INIT_FAIL').on(() => webglFailPopupRef.value?.showPopup());
    await sleep(100)
    renderScene();
    putEnvironmentAtScene();
    putBallAtScene();
    loadPlaneTexture();
    await sleep(1000);
    playKeyFrame();
}

const showBoard = ref(false);
const showStaticCoverText = ref(false);
const showMicro = ref(false);
const showAperture = ref(false);
const showClickBallTip = ref(false);
const showMilkFace = ref(false);
async function showMicroContainer() {
    showBoard.value = true;
    showStaticCoverText.value = true;
    await sleep(2500);
    showBoard.value = false;
    await sleep(1000);
    showMicro.value = true;
    setTimeout(onClickMicro, 1500);
}

const apertureAngle = ref(0);
function playApertureAnimation() {
    const update = createProgress(3000);
    const updateSpeed = createProgress(1000);
    const stageASpeed = 2;
    const stageBSpeed = 0.5;
    // const { stop } = playAudio('micro', true);
    const animate = (timestamp: number) => {
        const progress = update(timestamp);
        if (progress < 1) {
            apertureAngle.value += stageASpeed;
        } else {
            const changeSpeedProgress = updateSpeed(timestamp);
            const currentSpeed = lerp(stageASpeed, stageBSpeed, changeSpeedProgress);
            apertureAngle.value += currentSpeed;
            // if (changeSpeedProgress >= 0.6) stop();

        }
        requestAnimationFrame(animate);
    }
    requestAnimationFrame(animate);
}

const cupZoom = ref(1);
async function playZoomAnimation() {
    const update = createProgress(6000);
    await new Promise<void>(r => {
        const animate = (timestamp: number) => {
            const progress = update(timestamp);
            if (progress < 1) {
                cupZoom.value = Number(lerp(1, 3, progress).toFixed(2));
                requestAnimationFrame(animate);
            } else {
                r();
            }
        }
        requestAnimationFrame(animate);
    })
}

async function onClickMicro() {
    if (!showMicro.value) return;
    TK('scan_microscopeBtn_click', PageEnum.CUP, true);
    showMicro.value = false;    
    showStaticCoverText.value = false;
    await sleep(200);
    showAperture.value = true;
    showMilkFace.value = true;
    playApertureAnimation();
    showByMicro();
    // rotateZ(3500);
    TK('scan_microscopeAni_show', PageEnum.MICRO);
    showModel.value = true;
    showBalls.value = true;
    setTimeout(() => {
        showPlane();
        showClickBallTip.value = true;
    }, 4000);
    await playZoomAnimation();
    setTimeout(playBallInCenter, 3000);
}

const showEndSloganIndex = ref(0);
useIntervalFn(() => {
    showEndSloganIndex.value++;
    if (showEndSloganIndex.value >= 3) showEndSloganIndex.value = 0;
}, 5 * 1000, { immediate: true });
function onRotate(degree: number) {
    return;
    if (degree > -45 && degree <= -15) showEndSloganIndex.value = 0;
    if (degree > -15 && degree <= 15) showEndSloganIndex.value = 1;
    if (degree > 15 && degree <= 45) showEndSloganIndex.value = 2;
}

function onClickMoreBtn() {
    window.location.href = 'https://www.apta.com.hk/neobaby.html?utm_medium=QRcode&utm_source=QRcode&utm_campaign=Sep_NEO_ManningsPOSM_ARtool_generic&utm_content=neo';
    TK('scan_moreBtn_click', PageEnum.BALL, true);
}

function onClickGoldenBall() {
    TK('scan_compareBtn_click', PageEnum.BALL, true);
    navigateTo({ name: PageEnum.COVER, hash: 'Compare' });
    TK('compare_view', PageEnum.BALL);
}

const showVideo = ref(false);
const videoElement: Ref<HTMLVideoElement | null> = ref(null);
async function playVideo() {
    showVideo.value = true;
    await nextTick();
    videoElement.value?.play();
}
function hideVideo() {
    showVideo.value = false;
}
function onVideoEnd() {
    TK('scan_video_end', PageEnum.MICRO);
    hideVideo();
}

function restart() {
    showModel.value = false;
    showBalls.value = false;
    showImageContainer.value = true;
    showEndBtn.value = false;
    showEndSlogan.value = false;
    showOneCup.value = true;
    showBoard.value = false;
    showMicro.value = false;
    showAperture.value = false;
    showClickBallTip.value = false;
    showVideo.value = false;
    showEndSloganIndex.value = 0;
    setBloomStatus(false);
    resetModel();
}

onMounted(load);

defineExpose({
    playKeyFrame,
    restart,
});

</script>
<template>
    <div class="w-full h-full absolute top-0 left-0 flex items-center justify-center">
        <div class="w-full h-full absolute flex items-center justify-center" :class="{
            'pointer-events-none': !showModel,
            [showEndBtn ? 'z-10' : 'z-100']: true,
        }" ref="sceneRef" @click="modelEvent"></div>
        <transition>
            <img :src="getImageFile('one-cup.png')" class="w-[309px] object-contain top-25 absolute" v-if="showOneCup" />
        </transition>
        <div 
            class="image-container w-full h-full flex items-center justify-center cup-zoom-layer" 
            v-if="showImageContainer"
            :class="{
                [preventClickCup ? 'z-10' : 'z-11']: true,
                'pointer-events-none': preventClickCup,
            }"
            :style="{
                'transform': `scale(${cupZoom})`,
            }"
        >
            <img 
                v-for="index in showImageList" 
                :src="getImageFile(getKeyFrameFileName(index + keyStart - 1))" 
                :key="index"
                class="object-contain w-full absolute"
                :style="{
                    width: '220%',
                    transform: `
                        translateY(${(-0.5 * height + 0.5 * imageScale * width + imageOffsetY) * (index / 20)}px) 
                        scale(${100 + 10 * (index / 20)}%)
                    `,
                    opacity: (index + keyStart - 1) === key ? 1 : 0,
                    bottom: `${imageOffsetY}px`
                }"
            />
        </div>
        <transition name="fade2">
            <div class="milk-mask w-full h-full bg-[#F3F2EC] absolute z-10" v-if="showMilkFace" />
        </transition>
        <div class="interaction w-full h-full flex items-center justify-center absolute z-10">
            <transition>
                <img 
                    :src="getImageFile('board.png')" 
                    class="w-[204px] object-contain z-10" 
                    v-if="showBoard"
                    :style="{
                        transform: 'translate(25%, -25%)'
                    }"
                />
            </transition>
            <transition>
                <div class="flex flex-col w-full items-center absolute bottom-[25%]" v-if="showMicro">
                    <img :src="getImageFile('will-micro-text.png')" class="w-[270px] object-contain pulsate-fwd active:scale-95" @click="onClickMicro"/>
                    <!-- <img :src="getImageFile('micro.png')" class="w-[230px] object-contain pulsate-fwd active:scale-95" @click="onClickMicro"/> -->
                    <!-- <img :src="getImageFile('micro-milk-ball.png')" class="w-[311px] object-contain mt-4"> -->
                    <!-- <img :src="getImageFile('micro-tip.png')" class="w-[200px] object-contain mt-4" /> -->
                </div>
            </transition>
            <transition>
                <div class="flex flex-col w-full items-center absolute bottom-10" v-if="showStaticCoverText">
                    <img :src="getImageFile('static-cover-text.png')" class="h-[24px] object-contain" @click="onClickMicro"/>
                </div>
            </transition>
            <img 
                v-if="showAperture"
                :src="getImageFile('aperture.png')" 
                class="absolute w-full object-contain fade-in pointer-events-none"
                :style="{ 
                    width: '180%',
                    transform: `rotate(${apertureAngle}deg)`
                }"
            >
            <img
                v-if="showAperture"
                :src="getImageFile('micro-bottom-tip.png')"
                class="absolute h-[24px] object-contain bottom-6 pointer-events-none"
            >
            <transition>
                <div 
                    class="absolute w-full h-full bottom-0 flex justify-center"
                    v-if="showEndBtn"
                >
                    <img 
                        :src="getImageFile('end-bottom.png')"
                        class="w-full bottom-[-40px] object-contain absolute pointer-events-none"   
                    />
                    <img 
                        :src="getImageFile('copyright.png')"
                        class="w-[260px] bottom-3 object-contain absolute pointer-events-none"
                        :style="{ 
                            transformOrigin: 'bottom',
                            transform: `scale(${getScale()})`
                        }"
                    />
                </div>
            </transition>
            <transition>
                <div class="absolute w-full flex flex-col items-center justify-center translate-y-[150px]" :style="{ scale: getCenterScale() }" v-if="showEndBtn">
                    <img 
                        :src="getImageFile('slide-tip.png')"
                        class="w-[300px] object-contain"
                    >
                    <div class="top-[-20px] relative flex">
                        <img 
                            :src="getImageFile('compare.png')"
                            class="h-[58px] object-contain active:scale-95 mr-4"
                            @click="onClickGoldenBall"
                        >
                        <img 
                            :src="getImageFile('more.png')"
                            class="h-[58px] object-contain active:scale-95"
                            @click="onClickMoreBtn"
                        >
                    </div>
                </div>
            </transition>
        </div>
        <transition>
            <div 
                class="absolute w-full h-full bottom-0 z-9"
                :style="{
                    background: 'linear-gradient(180.55deg, #00000000 19.67%, #eaaa6a66 58.03%)'
                }"
                v-if="showEndBtn"
            >
            </div>
        </transition>
        <img 
            v-if="showClickBallTip"
            :src="isInTimeRange() ? getImageFile('click-ball-tip2.png') : getImageFile('click-ball-tip.png')"
            class="absolute w-[245px] object-contain scale-in-bl z-100 ml-[35px] mb-[285px]"
        >
        <!-- 三个小球 -->
        <div class="absolute w-full z-100 pointer-events-none" v-if="showBalls" :style="{
            width: `${width}px`,
            height: `${width}px`,
        }">
            <div class="w-full h-full relative">
                <img :src="getImageFile('golden-ball.png')" class="absolute bottom-[18%] right-[10%] object-contain w-[70px] ball1" />
                <div class="absolute bottom-[50%] right-[8%] -rotate-80">
                    <img :src="getImageFile('golden-ball.png')" class="object-contain w-[50px] ball2" />
                </div>
                <img :src="getImageFile('small-ball3.png')" class="absolute bottom-[15%] right-[45%] object-contain w-[30px] ball3" />
                <div class="absolute bottom-[60%] right-[25%] -rotate-80">
                    <img :src="getImageFile('small-ball2.png')" class="object-contain w-[40px] ball4" />
                </div>
                <div class="absolute bottom-[10%] right-[30%] -rotate-80">
                    <img :src="getImageFile('small-ball1.png')" class="object-contain w-[20px] ball5" />
                </div>      
                <div class="absolute bottom-[5%] right-[60%] rotate-80">
                    <img :src="getImageFile('small-ball1.png')" class="object-contain w-[45px] ball6" />
                </div>                              
            </div>
        </div>
        <!-- 三个 slogan -->
        <div class="absolute w-full h-full z-100 pointer-events-none" v-if="showEndSlogan">
            <div class="w-full h-full relative flex items-center justify-center">
                <div class="w-0 h-0 relative bottom-[60px]">
                    <div 
                        class="relative w-[150px] h-[190px] translate-y-[-100%]" 
                        :style="{
                            left: `${0 * getScale()}px`,
                            bottom: `${100 * getScale()}px`,
                        }"
                    >
                        <div class="relative w-full h-full">
                            <img 
                                v-for="(_, index) in 3"
                                v-if="!isInTimeRange()"
                                :src="getImageFile(`end-slogan${index}.png`)"
                                class="object-contain absolute z-101 scale-in-bl bottom-0 left-0"
                                :style="{
                                    width: `${160 * getScale()}px`
                                }"
                                v-show="showEndSloganIndex === index"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <img :src="getImageFile('logo.png')"  class="w-1/6 object-contain absolute left-1/15 top-8 z-10 pointer-events-none" />
        <!-- <transition name="fade">
            <video 
                ref="videoElement" 
                :width="width" 
                :height="height" 
                :src="getVideoFile('1.mp4')"
                muted 
                preload="auto"
                playsinline
                webkit-playsinline
                x5-video-player-type="h5-page"  
                x5-video-orientation="landscape|portrait" 
                class="max-w-full h-auto absolute"
                :class="[showVideo ? 'z-1000' : '-z-10', !showVideo && 'opacity-0']"
                @ended="onVideoEnd"
            />
        </transition> -->
        <WebglFailPopup ref="webglFailPopupRef" />
    </div>
</template>

<style>
.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade2-enter-active {
    transition: opacity 6s ease;  
}
.fade2-leave-active {
  transition: opacity 0.5s ease;
}

.fade2-enter-from,
.fade2-leave-to {
  opacity: 0;
}

.ball1 {
    -webkit-animation: scale-in-center 4s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
            animation: scale-in-center 4s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
    animation-delay: 1s
}
.ball2 {
    -webkit-animation: scale-in-center 4s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
            animation: scale-in-center 4s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
    animation-delay: 1.5s
}
.ball3 {
    -webkit-animation: scale-in-center 4s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
            animation: scale-in-center 4s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
    animation-delay: 2s
}
.ball4 {
    -webkit-animation: scale-in-center 4s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
            animation: scale-in-center 4s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
    animation-delay: 2.5s
}
.ball5 {
    -webkit-animation: scale-in-center 4s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
            animation: scale-in-center 4s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
    animation-delay: 3s
}
.ball6 {
    -webkit-animation: scale-in-center 4s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
            animation: scale-in-center 4s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
    animation-delay: 3.5s
}
</style>